import { useEffect } from 'react';
import { Bug, Home, RefreshCw } from 'lucide-react';
import { useRouteError } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { selectCurrentUser } from '@/redux/auth/auth.slice';

export default function ErrorBoundary() {
  const error = useRouteError() as Error;
  const isDevelopment = process.env.NODE_ENV === 'development';
  const { user } = useSelector(selectCurrentUser);

  useEffect(() => {
    if (!isDevelopment) {
      import('@sentry/react').then((Sentry) => {
        Sentry.setUser({
          id: user?._id,
          email: user?.email,
        });
        Sentry.setTags({
          section: 'Error Boundary',
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });
        Sentry.captureException(error, {
          level: 'fatal',
        });
      });
    }
  }, [error, user, isDevelopment]);

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg text-center space-y-8 flex flex-col items-center">
        <div className="space-y-4">
          <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center">
            <Bug className="h-12 w-12 text-red-700" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isDevelopment ? 'Development Error' : 'Oops! Tiny hiccup'}
            </h1>
            <p className="text-gray-600 text-lg">
              {isDevelopment
                ? 'An error occurred during development.'
                : 'This usually fixes itself with a quick refresh.'}
            </p>
          </div>
        </div>

        {isDevelopment && error && (
          <Card className="text-left ">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Bug className="h-5 w-5" />
                Error Details
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <code className="text-red-800 font-mono text-sm leading-relaxed">
                      {error.message}
                    </code>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg transition-all duration-200 max-h-96 overflow-y-auto">
                <pre className="p-4 text-lg font-mono text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-2 justify-center items-center">
          <Button onClick={handleReload} size="lg">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Page
          </Button>
          <Button onClick={handleGoHome} variant="outline" size="lg">
            <Home className="h-4 w-4 mr-2" />
            Go to Homepage
          </Button>
        </div>
        <p className="text-sm mb-2 text-muted-foreground">
          Our team has been automatically notified and is working on it.
        </p>

        {isDevelopment && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">Development mode active</p>
          </div>
        )}
      </div>
    </div>
  );
}
